import styled from '@emotion/styled';
import { useEffect, useMemo, useState } from 'react';
import { GAME_MODE, HighScoreEntity, SingSetup } from '~/interfaces';
import CameraManager from '~/modules/Camera/CameraManager';
import { Button } from '~/modules/Elements/Button';
import { sumDetailedScore } from '~/modules/GameEngine/GameState/Helpers/calculateScore';
import useKeyboardNav from '~/modules/hooks/useKeyboardNav';
import useViewportSize from '~/modules/hooks/useViewportSize';
import { PlayerScore } from '~/routes/Game/Singing/PostGame/PostGameView';
import CameraRoll from '~/routes/Game/Singing/PostGame/Views/Results/CameraRoll';
import { CameraRollPlaceholder } from '~/routes/Game/Singing/PostGame/Views/Results/CameraRollPlaceholder';
import PlayerScoreView from '~/routes/Game/Singing/PostGame/Views/Results/PlayerScore';

interface Props {
  onNextStep: () => void;
  players: PlayerScore[];
  highScores: HighScoreEntity[];
  singSetup: SingSetup;
}

function ResultsView({ onNextStep, players, highScores, singSetup }: Props) {
  const [segment, setSegment] = useState<number>(-1);
  const { width } = useViewportSize();
  const isMobile = (width ?? 9999) < 640;

  useEffect(() => {
    if (segment < 0) {
      setSegment(0);
    } else if (segment < 4) {
      const interval = setInterval(() => setSegment((s) => s + 1), 1_500);
      return () => clearInterval(interval);
    }
  }, [segment]);

  const isAnimFinished = segment > 3;

  const nextStep = () => {
    if (!isAnimFinished) setSegment(5);
    else onNextStep();
  };

  const { register } = useKeyboardNav();

  const isCoop = singSetup.mode === GAME_MODE.CO_OP;
  const finalPlayers = isCoop ? [{ ...players[0], name: players.map((p) => p.name).join(', ') }] : players;

  const playerScores = finalPlayers.map((p) => sumDetailedScore(p.detailedScore[0]));
  const highestScore = Math.max(...playerScores);

  const revealHighScore = segment > 3;

  const initialCameraPermission = useMemo(() => CameraManager.getPermissionStatus(), []);
  const [isRequestInProgress, setIsRequestInProgress] = useState(false);
  const enableCamera = () => {
    setIsRequestInProgress(true);
    CameraManager.requestPermissions().then(() => setIsRequestInProgress(false));
  };

  const cameraWide = !!(initialCameraPermission && revealHighScore);

  return (
    <Container data-mobile={isMobile}>
      <Main data-mobile={isMobile}>
        <ScoresColumn data-mobile={isMobile} data-collapse={revealHighScore}>
          {finalPlayers.map((player, idx) => (
            <PlayerScoreView
              key={idx}
              playerNumber={player.playerNumber}
              useColors={!isCoop}
              revealHighScore={revealHighScore}
              segment={segment}
              player={player}
              highScores={highScores}
              highestScore={highestScore}
              singSetup={singSetup}
            />
          ))}
        </ScoresColumn>

        <CameraColumn $wide={cameraWide} data-mobile={isMobile}>
          {initialCameraPermission ? (
            <CameraRoll />
          ) : (
            <CameraRollPlaceholder register={register} onConfirm={enableCamera} loading={isRequestInProgress} />
          )}
        </CameraColumn>
      </Main>

      {/* ✅ Sticky bottom action */}
      <BottomBar data-mobile={isMobile}>
        <SongSelectionButton
          {...register('next-button', () => nextStep(), undefined, true)}
          data-test={isAnimFinished ? 'highscores-button' : 'skip-animation-button'}
          data-mobile={isMobile}>
          {isAnimFinished ? 'Next' : 'Skip'}
        </SongSelectionButton>
      </BottomBar>
    </Container>
  );
}

export default ResultsView;

/* ---------------- Styles ---------------- */

const Container = styled.div`
  position: absolute;
  inset: 0;
  padding: 20rem 5rem 0 5rem;
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  height: 100%;
  overflow: hidden;

  display: flex;
  flex-direction: column;
  min-height: 0;

  &[data-mobile='true'] {
    padding: 7.5rem 1rem 0 1rem;
  }
`;

const Main = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4rem;
  flex: 1;
  width: 100%;
  max-width: 100%;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
  padding-bottom: 8rem;
  box-sizing: border-box;

  &[data-mobile='true'] {
    flex-direction: column;
    gap: 1rem;
  }
`;

const ScoresColumn = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 5rem;
  flex: 1;
  min-width: 0;
  max-width: 100%;
  min-height: 0;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  padding-right: 0.25rem;

  &[data-collapse='true'] {
    gap: 2rem;
  }

  transition: gap 0.5s;

  &[data-mobile='true'] {
    gap: 1rem;
  }
`;

const CameraColumn = styled.div<{ $wide?: boolean }>`
  flex: 0 1 ${(p) => (p.$wide ? '43%' : '28%')};
  width: ${(p) => (p.$wide ? '43%' : '28%')};
  max-width: ${(p) => (p.$wide ? '43%' : '28%')};
  transition: 300ms;
  min-height: 0;
  min-width: 0;
  overflow: hidden;

  &[data-mobile='true'] {
    width: 100%;
  }
`;

const BottomBar = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  z-index: 20;
  width: 100%;
  display: flex;
  justify-content: flex-end;
  box-sizing: border-box;
  padding: 1rem 5rem calc(env(safe-area-inset-bottom) + 1rem);
  background: linear-gradient(180deg, rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.72));

  &[data-mobile='true'] {
    padding-inline: 1rem;
  }
`;

const SongSelectionButton = styled(Button)`
  width: min(40rem, 100%);
  max-width: 100%;
  font-size: 1.9vw;

  &[data-mobile='true'] {
    width: 100%;
    font-size: clamp(18px, 5vw, 28px);
    border-radius: 18px;
    padding: 0.95rem 1rem;
  }
`;
